import { useCallback, useEffect, useId, useState, type FormEvent } from "react";
import { navigate, replaceQuery } from "@/lib/nav";
import { Loading, TableSkeleton } from "@/components/Loading";
import { Skeleton } from "@/components/ui/skeleton";
import type { SourceFile, DataChangeItem, IntegrityResponse, RecordHistoryResponse, RecordVersion, UploadChangesResponse } from "../../shared/api";
import { formatAuditTime } from "../../shared/audit";
import { formatNumber, integrityLine, parseRecordKey, recordKeyOf } from "../../shared/versions";
import { ErrorAlert } from "@/components/ErrorAlert";
import { Card, CardHeader } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { History, ShieldCheck, ShieldAlert } from "lucide-react";
import { PageHeader } from "@/components/PageHeader";
import { UserAvatar } from "@/components/UserAvatar";
import { VersionTimeline } from "@/components/VersionTimeline";
import { pathOf, screens } from "../screens";
import { NativeSelect } from "@/components/ui/native-select";
import { SeverityBadge, StatusBadge } from "@/components/StatusBadge";
import { findingStatusLabel, ruleLabel } from "../../shared/findings";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { errorText, getHistory, getIntegrity, getSources, getUploadChanges } from "@/lib/client";
import { SOURCE_TYPE_CODES, changeText, displayValue, fieldLabel } from "@/lib/upload";
import { SOURCE_TYPES, type SourceType } from "../../shared/rows";
import { sourceTypeText } from "../../shared/sources";
import { versionLabel } from "../../shared/upload";
import { recordIdText } from "./UploadDetailScreen";

/** Kimlik ipucu: seçili kaynak tipinin kimlik alanları (SOURCE_TYPES.key) ve kaçış kuralı (shared/versions.ts recordKeyOf). */
const keyFields = (sourceType: string) => (sourceType in SOURCE_TYPES ? SOURCE_TYPES[sourceType as SourceType].key : []);

export const SearchButton = ({ busy, ready }: { busy: boolean; ready: boolean }) => <Button type="submit" disabled={busy || !ready}>{busy ? "Aranıyor…" : "Ara"}</Button>;

/** Kaynak tipinin her kimlik alanı için ayrı etiketli kutu. */
export function IdentityInputs({ sourceType, parts, onChange }: { sourceType: string; parts: string[]; onChange: (i: number, v: string) => void }) {
  return <>{keyFields(sourceType).map((f, i) => (
    <div key={f} className="flex flex-col gap-2">
      <Label htmlFor={`h-key-${i}`}>{fieldLabel(f)}</Label>
      <Input id={`h-key-${i}`} value={parts[i] ?? ""} onChange={(e) => onChange(i, e.target.value)} required />
    </div>
  ))}</>;
}

/** Üst satır (spec §5): iki durum, ✓ ya da ✗. Metin tek kaynaktan (shared/versions.ts). Şerit kart; ikon ve metin birlikte. */
export function IntegrityBar({ integrity }: { integrity: IntegrityResponse | null }) {
  if (!integrity) return null;
  if (integrity.fileCount === 0 && integrity.failures.length === 0) return <p role="status" className="rounded-md border p-3 text-sm text-muted-foreground">Henüz dosya yüklenmedi.</p>;
  const bad = integrity.failures.length > 0, Icon = bad ? ShieldAlert : ShieldCheck;
  return (
    <div role="status" className={`flex items-start gap-3 rounded-xl border p-4 text-sm font-medium shadow-xs ${bad ? "border-critical-border bg-critical text-critical-foreground" : "border-success-border bg-success text-success-foreground"}`}>
      <Icon aria-hidden="true" className="mt-0.5 size-5 shrink-0" />
      <div>
        <p>Yüklenen dosyaların bütünlüğü: {integrityLine(integrity)}</p>
        {bad && (
          <ul className="mt-1 list-disc pl-5 font-normal">
            {integrity.failures.map((f) => <li key={f.uploadId}>{versionLabel("file", f.uploadId)} {f.file}: {f.result === "missing" ? "dosya yok" : "dosya değişmiş"}</li>)}
          </ul>
        )}
      </div>
    </div>
  );
}

/** Bir versiyon: yükleme no, gerçek zaman, dosya, parmak izi (8 hane), alan değerleri, gerekçe. Değişen alan işaretli. */
export function VersionView({ v, sourceType }: { v: RecordVersion; sourceType: string }) {
  const changed = new Set(v.changes.map((c) => c.field));
  const [all, setAll] = useState(false);
  const listId = useId();
  // Değişiklik yoksa (ilk versiyon, kayıt dönüşü) gösterecek fark olmadığından bütün alanlar görünür.
  const only = changed.size > 0 && !all;
  const entries = Object.entries(v.fields ?? {}).filter(([k]) => !only || changed.has(k));
  return (
      <Card className="gap-3 text-sm">
        <p className="flex flex-wrap items-center gap-x-4 gap-y-1">
          <b>{versionLabel(v.origin, v.uploadId)}</b>
          <span>{formatAuditTime(v.createdAt)}</span>
          {v.origin === "file" && <span>{v.fileName}</span>}
          <span>Parmak izi: <span className="font-mono" title={v.fileHash}>{v.fileHash.slice(0, 8)}</span></span>
          <span className="inline-flex items-center gap-1.5"><UserAvatar name={v.uploadedBy} />{v.uploadedBy}</span>
          {v.manualEntryId === v.uploadId && <StatusBadge tone="info">Elle girildi</StatusBadge>}
        </p>
        {v.present && v.fields ? (
          <dl id={listId} className="grid grid-cols-[max-content_1fr] gap-x-4 gap-y-1">
            {entries.map(([k, val]) => (
              <div key={k} className={`contents ${changed.has(k) ? "font-semibold" : ""}`}>
                <dt className="text-muted-foreground">{fieldLabel(k)}</dt>
                <dd className="tabular-nums">{displayValue(sourceType, k, val)}{changed.has(k) && <StatusBadge tone="warning" className="ml-2">Değişti</StatusBadge>}</dd>
              </div>
            ))}
          </dl>
        ) : (
          <p className="font-medium">Bu versiyonda kayıt yok (kaldırıldı).</p>
        )}
        {v.present && changed.size > 0 && <Button type="button" variant="outline" size="sm" className="self-start" aria-expanded={all} aria-controls={listId} onClick={() => setAll(!all)}>{all ? "Yalnız değişen alanları göster" : "Tüm alanları göster"}</Button>}
        {v.changes.length > 0 && (
          <ul className="font-mono text-xs tabular-nums">
            {v.changes.map((c) => <li key={c.field}>{fieldLabel(c.field)}: {changeText(sourceType, c.field, c.old, c.new)}</li>)}
          </ul>
        )}
        <p><span className="text-muted-foreground">Gerekçe:</span> {v.justification ?? "—"}</p>
      </Card>
  );
}

export function HistoryResult({ h }: { h: RecordHistoryResponse }) {
  return (
    <section aria-label="Versiyon listesi" className="flex flex-col gap-2">
      <h2 className="text-lg font-semibold">{sourceTypeText(h.sourceType)} / {recordIdText(h.sourceType, h.recordKey)} · {h.versions.length} versiyon</h2>
      <VersionTimeline items={[...h.versions].reverse().map((v) => ({ id: v.uploadId, children: <VersionView v={v} sourceType={h.sourceType} /> }))} />
    </section>
  );
}

export function ChangesList({ data, onOpen }: { data: UploadChangesResponse; onOpen?: (sourceType: string, recordKey: string) => void }) {
  const row = (i: DataChangeItem) => (
    <TableRow key={i.findingId} className="align-top">
      <TableCell className="px-4 text-sm">{ruleLabel[i.rule]}</TableCell>
      <TableCell><SeverityBadge severity={i.severity} /></TableCell>
      <TableCell className="font-medium">{recordIdText(data.sourceType, i.recordKey)}</TableCell>
      <TableCell className="text-muted-foreground">{i.status === "closed" ? (i.closeText ?? findingStatusLabel.closed) : findingStatusLabel.open}</TableCell>
      <TableCell className="whitespace-normal">
        {i.rule === "DATA_REMOVED" ? <span className="text-xs">Kayıt bu yüklemede yok.</span> : (
          <ul className="font-mono text-xs tabular-nums">{i.fields.map((c) => <li key={c.field}>{fieldLabel(c.field)}: {changeText(data.sourceType, c.field, c.old, c.new)}</li>)}</ul>
        )}
      </TableCell>
      <TableCell className="px-4 text-right">{onOpen && <button type="button" aria-label={`${recordIdText(data.sourceType, i.recordKey)} geçmişini göster`} className="rounded-sm text-primary underline underline-offset-4 outline-none focus-visible:ring-[3px] focus-visible:ring-ring" onClick={() => onOpen(data.sourceType, i.recordKey)}>Geçmişi göster</button>}</TableCell>
    </TableRow>
  );
  const th = "text-xs font-medium text-muted-foreground";
  return (
    <Card role="region" aria-labelledby="changes-h" className="gap-0 overflow-clip p-0">
      <CardHeader className="grid-rows-[auto] gap-1 border-b px-4 py-3 [.border-b]:pb-3">
        <h2 id="changes-h" className="text-base font-semibold">{versionLabel(data.origin, data.uploadId)}, {sourceTypeText(data.sourceType)}: {formatNumber(data.changedCount)} değişen, {formatNumber(data.removedCount)} kaldırılan</h2>
      </CardHeader>
      {data.items.length === 0 ? <p className="p-4 text-sm text-muted-foreground">Bu yükleme önceki versiyona göre değişiklik üretmedi.</p> : (
        <Table>
          <TableHeader>
            <TableRow className="bg-muted/60 hover:bg-muted/60">
              <TableHead className={`${th} px-4`}>Değişiklik</TableHead><TableHead className={th}>Şiddet</TableHead><TableHead className={th}>Kayıt</TableHead>
              <TableHead className={th}>Durum</TableHead><TableHead className={th}>Değişen alanlar</TableHead><TableHead className={th}><span className="sr-only">İşlem</span></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>{data.items.map(row)}</TableBody>
        </Table>
      )}
    </Card>
  );
}

/** E6 (spec §6). Salt okunur: yazma ve silme yolu yok. Hesap yok, her sayı API'den. */
export function HistoryScreen() {
  const [integrity, setIntegrity] = useState<IntegrityResponse | null>(null);
  const [changes, setChanges] = useState<UploadChangesResponse | null>(null);
  const [history, setHistory] = useState<RecordHistoryResponse | null>(null);
  const [sourceType, setSourceType] = useState("");
  const [parts, setParts] = useState<string[]>([]);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [uploads, setUploads] = useState<SourceFile[]>([]);
  const [uploadId, setUploadId] = useState("");

  useEffect(() => {
    getIntegrity().then(setIntegrity, (e) => setError(errorText(e)));
    getSources().then((s) => {
      const files = Object.values(s.boxes).flatMap((b) => b.files).sort((a, b) => b.uploadId - a.uploadId);
      setUploads(files);
      if (files[0]) setUploadId(String(files[0].uploadId));
    }).catch((e) => setError(errorText(e)));
  }, []);
  useEffect(() => {
    if (!uploadId) return;
    setError(""); setChanges(null);
    let stale = false; // hızlı seçici geçişinde eski istek geç dönerse yok sayılır
    getUploadChanges(Number(uploadId)).then((c) => { if (!stale) setChanges(c); }, (e) => { if (!stale) setError(errorText(e)); });
    return () => { stale = true; };
  }, [uploadId]);

  const search = useCallback(async (st: string, ps: string[]) => {
    setBusy(true); setError(""); setHistory(null);
    const key = recordKeyOf(ps.map((p) => p.trim()));
    replaceQuery("sourceType", st); replaceQuery("recordKey", key);
    try { setHistory(await getHistory(st, key)); } catch (e) { setError(errorText(e)); } finally { setBusy(false); }
  }, []);
  useEffect(() => { // ?sourceType=&recordKey= (E2 Kayıtlar bağlantısı): kutuları doldur, ara
    const q = new URLSearchParams(window.location.search);
    const st = q.get("sourceType") ?? "", key = q.get("recordKey") ?? "";
    const ps = parseRecordKey(key);
    if (key && ps.length === keyFields(st).length) { setSourceType(st); setParts(ps); void search(st, ps); }
  }, [search]);
  const submit = (e: FormEvent) => { e.preventDefault(); void search(sourceType, parts); };
  const ready = keyFields(sourceType).length > 0 && keyFields(sourceType).every((_, i) => parts[i]?.trim());

  return (
    <main className="flex flex-col gap-6 p-6">
      <PageHeader title="Veri Geçmişi" description={screens.find((x) => x.id === "E6")!.description} />
      {integrity ? <IntegrityBar integrity={integrity} /> : !error && <div aria-busy="true"><Loading><Skeleton className="h-12 w-full" /></Loading></div>}
      <form onSubmit={submit} aria-label="Kayıt arama" className="flex flex-wrap items-end gap-4 rounded-xl border bg-card p-4 shadow-xs">
        <div className="flex flex-col gap-2">
          <Label htmlFor="h-source">Kaynak tipi</Label>
          <NativeSelect id="h-source" value={sourceType} onChange={(e) => { setSourceType(e.target.value); setParts([]); setHistory(null); setError(""); }} required>
            <option value="">Seçin…</option>
            {SOURCE_TYPE_CODES.map((c) => <option key={c} value={c}>{sourceTypeText(c)}</option>)}
          </NativeSelect>
        </div>
        <IdentityInputs sourceType={sourceType} parts={parts} onChange={(i, v) => setParts((p) => { const n = [...p]; n[i] = v; return n; })} />
        <SearchButton busy={busy} ready={ready} />
      </form>
      <p className="text-sm text-muted-foreground">Kayıt kimliğini bilmiyorsanız <a href={pathOf("E2")} className="rounded-sm text-primary underline underline-offset-4 outline-none focus-visible:ring-[3px] focus-visible:ring-ring" onClick={(e) => navigate(e, pathOf("E2"))}>Veri Kaynakları</a> <span aria-hidden="true">›</span> yükleme detayından Geçmiş bağlantısını kullanın.</p>
      {error && <ErrorAlert>{error}</ErrorAlert>}
      {busy ? <div aria-busy="true"><TableSkeleton delay={0} /></div> : history && <HistoryResult h={history} />}
      {uploads.length > 0 && (
        <div className="flex flex-col gap-2">
          <Label htmlFor="h-upload">Yükleme</Label>
          <NativeSelect id="h-upload" value={uploadId} onChange={(e) => { setChanges(null); setUploadId(e.target.value); }}>
            {uploads.map((u) => <option key={u.uploadId} value={u.uploadId}>{versionLabel(u.origin, u.uploadId)} · {sourceTypeText(u.sourceType)} · {u.file}</option>)}
          </NativeSelect>
        </div>
      )}
      {uploads.length > 0 && !changes && !error && <div aria-busy="true"><TableSkeleton cols={6} /></div>}
      {changes && <ChangesList data={changes} onOpen={(st, k) => { const ps = parseRecordKey(k); setSourceType(st); setParts(ps); void search(st, ps); }} />}
    </main>
  );
}
